"use client";

import { useRouter } from "next/router";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from "react";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

type DataLayerEcommerceItem = {
  item_id: string;
  item_brand: string;
  item_category: string;
  item_name: string;
  item_variant: string;
  price: number;
  quantity: number;
};

type DataLayerEcommerce = {
  [key: string]: string | number | Array<DataLayerEcommerceItem>;
  currency: string;
  items: Array<DataLayerEcommerceItem>;
  value: number;
};

type SearchEvent = {
  event: "search";
  search_term: string;
};

type PageViewEvent = {
  event: "page_view";
  page_path: string;
  page_title: string;
};

type EcommerceEvent = {
  event: "add_to_cart" | "view_item" | "begin_checkout" | "purchase";
  ecommerce: DataLayerEcommerce;
};

type DataLayerEvent = EcommerceEvent | SearchEvent | PageViewEvent;

type DatalayerContextProps = {
  push: (ev: DataLayerEvent) => void;
};

// eslint-disable-next-line strict-null-checks/all
const DatalayerContext = createContext<DatalayerContextProps | null>(null);

export const useDatalayer = () => {
  const context = useContext(DatalayerContext);

  if (!context) {
    throw new Error("useDatalayer must be used within a DatalayerProvider");
  }

  return context;
};

export const DatalayerProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();

  // Track page views when pathname changes
  useEffect(() => {
    const handleRouteChange = () => {
      if (typeof window !== "undefined") {
        push({
          event: "page_view",
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }
    };

    handleRouteChange();
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  const push = useCallback((event: DataLayerEvent) => {
    if (typeof window === "undefined" || !window.dataLayer) {
      return;
    }

    try {
      window.dataLayer.push(event);
      // Assumption: If dataLayer is defined, it includes a functional push method as provided by GTM
      // eslint-disable-next-line no-empty
    } catch {}
  }, []);

  return (
    <DatalayerContext.Provider
      value={{
        push,
      }}
    >
      {children}
    </DatalayerContext.Provider>
  );
};
